import React from "react";
import { PlusOutlined } from "@ant-design/icons";
import { Button, Collapse, Empty, Typography } from "antd";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";

import { type TeamMemberTaskWithTrainee } from "@fitness-app/data-models/entities/TeamMemberTask";

import { useTeamMemberTaskContext } from "../context/TeamMemberTaskContext";
import { DraggableTaskItem } from "./DraggableTaskItem";
import { TeamMemberTaskItem } from "./TeamMemberTaskItem";

interface TaskDateSectionProps {
  title: string;
  date?: string | null;
  tasks: TeamMemberTaskWithTrainee[];
  isOverdue?: boolean;
  draggable?: boolean;
  showAddButton?: boolean;
  defaultOpen?: boolean;
  isOpen?: boolean;
  onToggle?: (isOpen: boolean) => void;
  showTaskDate?: boolean;
}

export const TaskDateSection: React.FC<TaskDateSectionProps> = ({
  title,
  date,
  tasks,
  isOverdue = false,
  draggable = false,
  showAddButton = true,
  defaultOpen = true,
  isOpen,
  onToggle,
  showTaskDate = false,
}) => {
  const { t } = useTranslation("dashboard");
  const { openTaskForm } = useTeamMemberTaskContext();

  const handleAddTask = () => {
    openTaskForm({
      dueDate: {
        date: date || dayjs().format("YYYY-MM-DD"),
        time: null,
        timezone: "Europe/Warsaw",
      },
    });
  };

  const formatDateHeader = () => {
    if (!date) return title;

    const taskDate = dayjs(date);
    const today = dayjs().startOf("day");

    if (taskDate.isSame(today, "day")) {
      return t("tasks.today");
    }

    if (taskDate.isSame(today.add(1, "day"), "day")) {
      return t("tasks.tomorrow");
    }

    // Format as "D MMMM" (e.g., "28 stycznia")
    return taskDate.format("D MMMM");
  };

  const taskCount = tasks.length;

  // Use controlled state if isOpen is provided, otherwise use defaultOpen
  const collapseProps =
    isOpen !== undefined
      ? {
          activeKey: isOpen ? ["1"] : [],
          onChange: (keys: string | string[]) => onToggle?.(Array.isArray(keys) ? keys.length > 0 : Boolean(keys)),
        }
      : { defaultActiveKey: defaultOpen ? ["1"] : [] };

  return (
    <Collapse
      {...collapseProps}
      ghost
      className="task-date-section"
      items={[
        {
          key: "1",
          label: (
            <div className="flex items-center gap-2">
              <Typography.Text
                strong
                type={isOverdue ? "danger" : undefined}
                className={isOverdue ? "text-red-600" : ""}
              >
                {formatDateHeader()}
              </Typography.Text>
              <Typography.Text type="secondary" className="text-sm">
                ({taskCount})
              </Typography.Text>
            </div>
          ),
          children: (
            <div className="space-y-2">
              {tasks.length === 0 && (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={t("tasks.noTasksInCategory")}
                  className="py-2"
                />
              )}

              {tasks.map((task) =>
                draggable ? (
                  <DraggableTaskItem key={task.id} task={task} showDate={showTaskDate} />
                ) : (
                  <TeamMemberTaskItem key={task.id} task={task} showDate={showTaskDate} />
                ),
              )}

              {showAddButton && (
                <Button type="dashed" icon={<PlusOutlined />} onClick={handleAddTask} className="w-full">
                  {t("tasks.addTask")}
                </Button>
              )}
            </div>
          ),
        },
      ]}
    />
  );
};
